import React, { useEffect, useState } from "react";
import { useStatusContext } from "../Page/status_context";
import { useSidebar } from "../ui/sidebar";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHeader, TableHead, TableRow } from "../ui/table";
import { Pencil } from "lucide-react";
import LoadingScreenDenda from "../Unique/Animation/loading_screen";

const updateChallenge = (gameId, date, newChallenge) => {
  fetch(process.env.REACT_APP_CMC_API + "/statistics", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      method: "updateDailyChallenge",
      game: gameId,
      date: date,
      info: newChallenge,
    }),
  })
    .then((response) => {
      if (response.ok) {
        // Handle successful response
      } else {
        // Handle error response
        throw new Error("Failed to update challenge");
      }
    })
    .catch((error) => {
      // Handle error here
      console.error("Error updating challenge:", error);
    });
};

const ChallengeViewer = ({ game_id }) => {
  const { dates, setAuthenticated } = useSidebar();
  const { setStatus, setLoading } = useStatusContext();
  const [challenges, setChallenges] = useState([]);
  const [error, setError] = useState("");
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [selectedOption, setSelectedOption] = useState("");
  const [enteredNumber, setEnteredNumber] = useState("");
  const [rowID, setRowID] = useState(0);

  useEffect(() => {
    if (game_id === null) return;
    setLoading(true); // Set loading state to false after login attempt
    const url = process.env.REACT_APP_CMC_API + "/statistics?method=getDailyChallenge&gameid=" + game_id + "&date=" + dates[0] + "_2100-01-01";
    const fetchData = async () => {
      setStatus({ text: "Fetching data...", color: 2 });
      await fetch(url, {
        headers: {
          Connection: "close",
        },
        credentials: "include",
      })
        .then((response) => {
          if (response.status === 200) {
            return response.json();
          } else if (response.status === 401) {
            setAuthenticated(false);
          } else {
            throw new Error("Failed to fetch challenges");
          }
        })
        .then((data) => {
          // 2. Map directly over data
          const challenges = data.date.map((date, index) => {
            return {
              id: index + 1, // Increment index by 1 to start id from 1 instead of 0
              date: date,
              challenge: data.daily_challenge_info[index], // Match each date with the corresponding challenge
            };
          });

          // 3. Reverse the challenges array if you need that ordering
          challenges.reverse();

          // 4. Save the result
          setChallenges(challenges);
          setStatus({ text: "Fetching successful!", color: 0 });
        })
        .catch((error) => {
          setStatus({ text: "Error fetching data", color: 1 });
          setError(error.message);
        });
      setStatus({ text: "Fetching successful...", color: 0 });
    };
    fetchData();
    setLoading(false);
  }, [dates, game_id, setLoading, setStatus]);

  if (error) {
    return <div>Error fetching challenges: {error}</div>;
  }

  const handleEditClick = (row) => {
    setSelectedChallenge(row); // Assuming row contains all the data you need
    setRowID(row.id);
    const [challenge, value] = row.challenge.split("-");
    setEnteredNumber(value); // Update the value input
    setSelectedOption(challenge); // Set the dropdown selection
    setOpenPopup(true); // Finally, open the popup
  };

  const handleSave = async () => {
    updateChallenge(game_id, selectedChallenge.date, `${selectedOption}-${enteredNumber}`);
    const updatedChallenge = {
      id: rowID, // Ensure this matches the identifier in the challenges array
      date: selectedChallenge.date,
      challenge: `${selectedOption}-${enteredNumber}`, // Recreate the challenge string or however it should be formatted
    };

    // Now, update the challenges state with the updated challenge
    const updatedChallenges = challenges.map((challenge) => (challenge.id === rowID ? updatedChallenge : challenge));

    setChallenges(updatedChallenges);
    handleClosePopup(); // Close the popup when done
    // Optionally reset the originalDate and other states if necessary
  };

  const handleClosePopup = () => {
    // Reset state variables to their defaults
    setOpenPopup(false);
    setEnteredNumber("");
    setSelectedOption("");
    setRowID(0);
    // Any other state resets as necessary
  };

  const getMenuItems = () => {
    if (game_id === 1 || game_id === 6 || game_id === 7) {
      return [
        { value: "clear", label: "clear" },
        { value: "reach", label: "reach" },
      ];
    } else if (game_id === 2 || game_id === 10) {
      return [
        { value: "combo", label: "combo" },
        { value: "speed", label: "speed" },
        { value: "time", label: "time" },
      ];
    } else if (game_id === 9 || game_id === 11 || game_id === 3 || game_id === 12) {
      return [{ value: "placeholder", label: "placeholder" }];
    } else if (game_id === 13 || game_id === 14) {
      return [{ value: "difficulty", label: "difficulty" }];
    } else {
      return [];
    }
  };

  return (
    <div className="w-screen !max-w-[85vw] md:max-w-full px-4">
      {challenges.length === 0 ? (
        <LoadingScreenDenda text="Loading..." />
      ) : (
        <div className="rounded-md border-[#20242b] h-[600px] overflow-hidden">
          <div className="overflow-y-auto h-full">
            <Table className={"dark:text-white" }>
              <TableHeader className="bg-purple-700 top-0 z-10">
                <TableRow>
                  <TableHead className="text-primary-foreground font-bold w-32">Date</TableHead>
                  <TableHead className="text-primary-foreground font-bold">Challenges</TableHead>
                  <TableHead className="text-primary-foreground font-bold w-20">Edit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {challenges.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium whitespace-nowrap">{row.date}</TableCell>
                    <TableCell className="max-w-[300px] truncate">{row.challenge}</TableCell>
                    <TableCell className="text-center">
                      <Button variant="outline" size="sm" onClick={() => handleEditClick(row)} className="h-8 w-8 p-0">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog open={openPopup} onOpenChange={setOpenPopup}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogTitle className="text-lg font-semibold">Challenge Details</DialogTitle>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Select value={selectedOption} onValueChange={setSelectedOption}>
                <SelectTrigger>
                  <SelectValue placeholder="Select challenge type" />
                </SelectTrigger>
                <SelectContent>
                  {getMenuItems().map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Input type="number" value={enteredNumber} onChange={(e) => setEnteredNumber(e.target.value)} className="mt-2" />
            </div>

            <Button onClick={handleSave} className="mt-2">
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChallengeViewer;
